/* eslint-disable @typescript-eslint/no-unsafe-return */
import axios from 'axios'

import { V1_API_URL } from '@src/configs/env'
import { BaseURL } from '@src/libs/api'
import { IConfigResponse, IDeploymentConfig } from '@src/types/config.api'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('config.api')

// Debug settings for simulating slow API responses
// These should only be used during development or testing
export const DEBUG_CONFIG = {
    // Delay in milliseconds (0 means no delay)
    apiDelayMs: 10000,

    // Helper method to introduce a delay for debugging
    simulateDelay: async (): Promise<void> => {
        if (DEBUG_CONFIG.apiDelayMs > 0) {
            logger.info(`DEBUG: Simulating API delay of ${DEBUG_CONFIG.apiDelayMs}ms`)
            await new Promise(resolve => setTimeout(resolve, DEBUG_CONFIG.apiDelayMs))
        }
    },

    // Helper method to introduce a delay for debugging
    simulateDelayedCrash: async (): Promise<void> => {
        if (DEBUG_CONFIG.apiDelayMs > 0) {
            logger.info(`DEBUG: Simulating API delay of ${DEBUG_CONFIG.apiDelayMs}ms`)
            await new Promise((resolve, reject) =>
                setTimeout(() => {
                    reject(new Error('Simulated API crash'))
                }, DEBUG_CONFIG.apiDelayMs)
            )
        }
    },
}

interface ChainModel {
    name: string
    symbol: string
    type: string
    id: number | string
    lz_id?: number
    cctp_domain?: number
    contract_info?: {
        order_book?: string
        cctp_token_messenger?: string
        cctp_v1_token_messenger?: string
        // Additional contract addresses may be added here as needed
    }
}

type V2ChainsResponse = Record<string, ChainModel>

/**
 * Transforms V2 API chain response to the format expected by IConfigResponse
 */
const transformV2ChainResponse = (chainsData: V2ChainsResponse): IConfigResponse => {
    logger.info('Transforming V2 API chain data to legacy format')

    const deployments: Record<string, IDeploymentConfig> = {}

    // Transform each chain entry to match the expected deployment format
    Object.entries(chainsData).forEach(([chainKey, chainData]) => {
        // Convert the chain name to lowercase for consistency
        const chainName = chainKey.toLowerCase()

        // Extract chain ID
        const chainId = typeof chainData.id === 'string' ? parseInt(chainData.id, 10) : chainData.id

        // Extract CCTP domain ID or default to 0 if not available
        const cctpId = chainData.cctp_domain ?? 0

        // Extract Layer Zero ID or default to 0 if not available
        const lzId = chainData.lz_id ?? 0

        // Create contracts object from contract_info or empty object if not available
        const contracts = chainData.contract_info
            ? {
                  order_book: chainData.contract_info.order_book,
                  order_book_v2: chainData.contract_info.order_book, // Map same address to both order_book and v2
                  cctp_token_messenger:
                      chainData.contract_info.cctp_token_messenger || chainData.contract_info.cctp_v1_token_messenger, // Add CCTP token messenger address
                  // Map more contracts as they become available
              }
            : {}

        logger.info(`Mapped contract addresses for ${chainName}:`, contracts)

        // Create a deployment config entry that matches the expected format
        deployments[chainName] = {
            abbreviation: chainData.symbol.toUpperCase(),
            chain_id: chainId,
            contracts: contracts,
            lz_cid: lzId,
            cctp_id: cctpId,
            chain_type: chainData.type,
        }
    })

    return { deployments }
}

/**
 * Gets specific chain information by chain name
 * This is a new endpoint available in V2 API
 */
export const apiGetChainInfo = async (chainName: string): Promise<IDeploymentConfig | null> => {
    try {
        // Apply debug delay if configured
        // await DEBUG_CONFIG.simulateDelayedCrash()

        if (!isFeatureEnabled('V2_MIGRATION')) {
            logger.info('V2 API not enabled, cannot fetch specific chain info')
            return null
        }

        logger.info(`Using V2 API to get info for chain: ${chainName}`)
        const { data } = await BaseURL.get<Record<string, ChainModel>>('/info/chains')

        // Find the chain by name (case-insensitive)
        const normalizedChainName = chainName.toLowerCase()
        const chainEntry = Object.entries(data).find(([key]) => key.toLowerCase() === normalizedChainName)

        if (!chainEntry) {
            logger.warn(`Chain not found: ${chainName}`)
            return null
        }

        // Transform single chain entry to deployment config
        const [_key, chainData] = chainEntry
        const chainId = typeof chainData.id === 'string' ? parseInt(chainData.id, 10) : chainData.id

        return {
            abbreviation: chainData.symbol.toUpperCase(),
            chain_id: chainId,
            contracts: {},
            lz_cid: 0,
            cctp_id: 0,
            chain_type: chainData.type,
        }
    } catch (error) {
        logger.error(`Error fetching chain info for ${chainName}:`, error)
        return null
    }
}

export const apiGetConfig = async (): Promise<IConfigResponse> => {
    try {
        // Apply debug delay if configured
        // await DEBUG_CONFIG.simulateDelayedCrash()

        // Use V2 API when the feature flag is enabled
        if (isFeatureEnabled('V2_MIGRATION')) {
            logger.info('Using V2 API for chain configuration')
            const { data } = await BaseURL.get<V2ChainsResponse>('/info/chains')
            return transformV2ChainResponse(data)
        }

        // Use legacy API when V2 is disabled
        logger.info('Using legacy API for chain configuration')
        // Create a specific axios instance with the legacy API URL to ensure correct URL
        const legacyApi = axios.create({
            baseURL: V1_API_URL,
        })

        const { data } = await legacyApi.get<IConfigResponse>('/get_config')
        return data
    } catch (error) {
        logger.error('Error fetching chain configuration:', error)
        throw error // Let the caller handle the final error
    }
}
